import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, ExternalLink, Bitcoin, Copy } from "lucide-react";
import { toast } from "sonner";

const TIER_LABEL = { free: "Free", pro: "Pro", team: "Team" };

const formatExpiry = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return null;
  }
};

export const SubscriptionCard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [billingStatus, setBillingStatus] = useState(null);

  // Poll while a pending crypto invoice exists so confirmations stay fresh.
  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const fetchStatus = async () => {
      try {
        const r = await api.get("/billing/status");
        if (cancelled) return;
        setBillingStatus(r.data);
        if (r.data?.pending_invoice) {
          timer = setTimeout(fetchStatus, 6000);
        }
      } catch { /* swallow */ }
    };
    fetchStatus();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [user?.subscription_tier]);

  const tier = user?.subscription_tier || "free";
  const isPaid = tier !== "free";
  const expiry = formatExpiry(billingStatus?.current_period_end);
  const pending = billingStatus?.pending_invoice;

  return (
    <div className="border border-[#262626] p-6" data-testid="subscription-card">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Subscription</div>
      <div className="flex items-center gap-2 text-sm">
        <Sparkles size={16} className={isPaid ? "text-emerald-400" : "text-zinc-500"} />
        <span data-testid="subscription-tier-label">Quill {TIER_LABEL[tier]}</span>
        {billingStatus?.status && isPaid && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
            · {billingStatus.status}
          </span>
        )}
      </div>

      {isPaid && expiry && (
        <div className="mt-3 text-xs text-zinc-500" data-testid="subscription-expiry">
          Renews/expires <span className="text-zinc-300">{expiry}</span>
          {billingStatus?.subscription_chain && (
            <> · paid in <span className="text-zinc-300 uppercase">{billingStatus.subscription_chain}</span></>
          )}
        </div>
      )}

      {pending && (
        <div
          className="mt-4 border border-amber-500/40 bg-amber-500/5 p-3 text-[11px] text-amber-200"
          data-testid="pending-sub-invoice-banner"
        >
          <div className="font-medium">
            {pending.status === "detected"
              ? "Payment detected · counting confirmations"
              : "Open subscription invoice"}
          </div>
          <div className="text-amber-300/80 mt-1">
            {pending.amount_crypto} {pending.chain.toUpperCase()}
            {" "}for Quill {TIER_LABEL[pending.tier]} · {pending.interval}
          </div>
          <button
            onClick={() => navigate(`/checkout/${pending.sub_invoice_id}`)}
            data-testid="resume-checkout-btn"
            className="mt-3 inline-flex items-center gap-1.5 text-amber-100 hover:text-white text-[11px] uppercase tracking-[0.22em]"
          >
            Resume checkout <ExternalLink size={11} />
          </button>
        </div>
      )}

      <div className="mt-4 flex gap-2 flex-wrap">
        {!isPaid ? (
          <a
            href="/pricing"
            data-testid="upgrade-link"
            className="bg-white text-black text-sm font-medium px-4 py-2 rounded-md hover:bg-zinc-200 inline-flex items-center gap-2"
          >
            Upgrade plan
          </a>
        ) : (
          <a
            href="/pricing"
            data-testid="settings-manage-subscription-btn"
            className="border border-[#262626] hover:border-white text-zinc-300 hover:text-white text-sm px-4 py-2 rounded-md inline-flex items-center gap-2"
          >
            Change plan
          </a>
        )}
      </div>

      {billingStatus?.platform_wallets && (
        <div className="mt-5 pt-4 border-t border-[#262626]" data-testid="platform-wallets-block">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-2">Platform wallets</div>
          <p className="text-[11px] text-zinc-500 leading-relaxed mb-3">
            Subscription payments land directly at these self-custody addresses.
            Quill never holds your funds.
          </p>
          <div className="space-y-2">
            {["eth", "btc"].map((c) => {
              const addr = billingStatus.platform_wallets[c];
              if (!addr) return null;
              return (
                <div key={c} className="flex items-center gap-2 text-[11px]" data-testid={`platform-wallet-${c}`}>
                  <Bitcoin size={12} className={c === "btc" ? "text-amber-400" : "text-zinc-500"} />
                  <span className="text-zinc-500 uppercase w-7">{c}</span>
                  <span className="font-mono text-zinc-300 truncate flex-1">{addr}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(addr);
                      toast.success(`${c.toUpperCase()} address copied`);
                    }}
                    title="Copy address"
                    aria-label={`Copy ${c.toUpperCase()} platform wallet address`}
                    data-testid={`copy-platform-wallet-${c}`}
                    className="p-2.5 -my-1 text-zinc-500 hover:text-white rounded-md hover:bg-[#241c17]"
                  >
                    <Copy size={13} strokeWidth={2} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
